import React, { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import QRCode from "qrcode";
import { Html5QrcodeScanner } from "html5-qrcode";
import { motion, AnimatePresence } from "framer-motion";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

/**
 * App de gestion des boîtes + QR (cave) — fichier unique React
 * Corrections apportées :
 * - Ajout des fonctions loadDB / saveDB manquantes (corrige ReferenceError)
 * - Ajout d'un export PDF fiable (utilise `qrcode` + `pdf-lib`)
 * - Code autonome (tout en localStorage)
 *
 * Dépendances à installer :
 * npm install qrcode pdf-lib qrcode.react html5-qrcode framer-motion
 */

// ====== Constantes & Helpers ======
const STORAGE_KEY = "qr-cave-db-v1";
const uuid = () => (crypto?.randomUUID ? crypto.randomUUID() : (Math.random().toString(16).slice(2) + Date.now().toString(16)));
const nowISO = () => new Date().toISOString();

function saveDB(db) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
  } catch (e) {
    console.error("Impossible de sauvegarder la DB", e);
  }
}

function loadDB() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { boites: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.boites)) return { boites: [] };
    return parsed;
  } catch (e) {
    console.warn("DB invalide, retour à une DB vide", e);
    return { boites: [] };
  }
}

function download(filename, text) {
  const blob = new Blob([text], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ====== Petits composants UI ======
function Section({ title, children, right }) {
  return (
    <div className="w-full max-w-6xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-4">
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
        {right}
      </div>
      <div className="bg-white/70 dark:bg-zinc-900/70 rounded-2xl shadow p-4 md:p-6 border border-zinc-200 dark:border-zinc-800">
        {children}
      </div>
    </div>
  );
}

function Pill({ children }) {
  return <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">{children}</span>;
}

function Input({ className = "", ...props }) {
  return <input className={`w-full px-3 py-2 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${className}`} {...props} />
}

function Textarea({ className = "", ...props }) {
  return <textarea className={`w-full px-3 py-2 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${className}`} {...props} />
}

function Button({ children, className = "", variant = "primary", ...props }) {
  const base = "px-3 py-2 rounded-2xl text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-indigo-600 hover:bg-indigo-700 text-white",
    ghost: "bg-transparent hover:bg-zinc-100 dark:hover:bg-zinc-800 border border-zinc-300 dark:border-zinc-700",
    danger: "bg-red-600 hover:bg-red-700 text-white",
    subtle: "bg-zinc-200/70 hover:bg-zinc-300/70 dark:bg-zinc-800/70 dark:hover:bg-zinc-700/70",
  };
  return <button className={`${base} ${variants[variant] || variants.primary} ${className}`} {...props}>{children}</button>;
}

function Badge({ children }) {
  return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800">{children}</span>;
}

// ====== Modal d'édition d'une boîte ======
function EditBoiteModal({ open, onClose, onSave, initial }) {
  const [nom, setNom] = useState(initial?.nom ?? "");
  const [emplacement, setEmplacement] = useState(initial?.emplacement ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [items, setItems] = useState(initial?.items ?? []);

  React.useEffect(() => {
    if (open) {
      setNom(initial?.nom ?? "");
      setEmplacement(initial?.emplacement ?? "");
      setNotes(initial?.notes ?? "");
      setItems(initial?.items ?? []);
    }
  }, [open, initial]);

  const addItem = () => setItems(prev => [...prev, { id: uuid(), nom: "Chaise", quantite: 1, notes: "" }]);
  const updateItem = (id, patch) => setItems(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it));
  const removeItem = (id) => setItems(prev => prev.filter(it => it.id !== id));

  const canSave = nom.trim().length > 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }} className="w-full max-w-2xl bg-white dark:bg-zinc-950 rounded-2xl shadow-xl border border-zinc-200 dark:border-zinc-800">
            <div className="p-5 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{initial ? "Modifier la boîte" : "Nouvelle boîte"}</h3>
              <Button variant="ghost" onClick={onClose}>Fermer</Button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Nom *</label>
                  <Input value={nom} onChange={e => setNom(e.target.value)} placeholder="Ex. Boîte A - Chaises pliantes" />
                </div>
                <div>
                  <label className="text-sm font-medium">Emplacement</label>
                  <Input value={emplacement} onChange={e => setEmplacement(e.target.value)} placeholder="Ex. Cave rangée 2, étagère du bas" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Infos utiles…" />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Articles (chaises)</h4>
                  <Button onClick={addItem} variant="subtle">+ Ajouter</Button>
                </div>
                {items.length === 0 && (
                  <div className="text-sm text-zinc-500">Aucun article pour l’instant.</div>
                )}
                <div className="space-y-3">
                  {items.map((it) => (
                    <div key={it.id} className="grid md:grid-cols-12 gap-2 items-center p-3 rounded-xl border border-zinc-200 dark:border-zinc-800">
                      <div className="md:col-span-6">
                        <Input value={it.nom} onChange={e => updateItem(it.id, { nom: e.target.value })} placeholder="Nom (ex. Chaise pliante noire)" />
                      </div>
                      <div className="md:col-span-2">
                        <Input type="number" min={0} value={it.quantite} onChange={e => updateItem(it.id, { quantite: Number(e.target.value) })} placeholder="Qté" />
                      </div>
                      <div className="md:col-span-3">
                        <Input value={it.notes ?? ""} onChange={e => updateItem(it.id, { notes: e.target.value })} placeholder="Notes (optionnel)" />
                      </div>
                      <div className="md:col-span-1 text-right">
                        <Button variant="danger" onClick={() => removeItem(it.id)}>Suppr</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-zinc-200 dark:border-zinc-800 flex items-center justify-end gap-3">
              <Button variant="ghost" onClick={onClose}>Annuler</Button>
              <Button onClick={() => onSave({ nom, emplacement, notes, items })} disabled={!canSave}>Enregistrer</Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ====== QR Label component (affiche + télécharge le QR) ======
function QRLabel({ boite }) {
  const ref = useRef(null);
  const value = `box:${boite.id}`;
  const fileName = `QR-${(boite.nom || 'boite').replace(/[^a-z0-9_\-]/gi, '_')}.png`;

  const downloadQR = () => {
    const canvas = ref.current?.querySelector("canvas");
    if (canvas) {
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
    } else {
      alert("Impossible de trouver le canvas QR pour le téléchargement.");
    }
  };

  return (
    <div className="flex flex-col md:flex-row items-center gap-4">
      <div ref={ref} className="p-3 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <QRCodeCanvas value={value} size={180} includeMargin={true} />
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-lg truncate">{boite.nom}</div>
        <div className="text-sm text-zinc-600 dark:text-zinc-400">ID: <code className="select-all">{boite.id}</code></div>
        {boite.emplacement && <div className="text-sm">📦 {boite.emplacement}</div>}
        <div className="mt-2 flex gap-2">
          <Button onClick={downloadQR}>Télécharger le QR</Button>
        </div>
      </div>
    </div>
  );
}

// ====== Scanner (html5-qrcode) ======
function Scanner({ onResult }) {
  const containerRef = useRef(null);
  const scannerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (scannerRef.current) return;

    const scanner = new Html5QrcodeScanner(
      "qr-reader",
      { fps: 10, qrbox: 250, aspectRatio: 1.0, rememberLastUsedCamera: true },
      false
    );

    scanner.render(
      (decodedText) => {
        try {
          onResult(decodedText);
        } catch (e) {
          console.error(e);
        }
      },
      (err) => {
        // erreurs de scan non bloquantes
      }
    );

    scannerRef.current = scanner;

    return () => {
      try { scanner.clear(); } catch (e) { }
      scannerRef.current = null;
    };
  }, [onResult]);

  return (
    <div ref={containerRef} className="w-full">
      <div id="qr-reader" className="w-full" />
    </div>
  );
}

// ====== Carte boîte ======
function BoiteCard({ boite, onEdit, onDelete }) {
  const total = boite.items.reduce((s, it) => s + (Number(it.quantite) || 0), 0);
  return (
    <motion.div layout className="p-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white/70 dark:bg-zinc-900/70 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-lg truncate">{boite.nom}</h4>
            <Badge>{total} articles</Badge>
          </div>
          {boite.emplacement && <div className="text-sm text-zinc-600 dark:text-zinc-400">📍 {boite.emplacement}</div>}
          {boite.notes && <div className="text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2 mt-1">📝 {boite.notes}</div>}
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="ghost" onClick={() => onEdit(boite)}>Modifier</Button>
          <Button variant="danger" onClick={() => onDelete(boite.id)}>Supprimer</Button>
        </div>
      </div>
      {boite.items.length > 0 && (
        <div className="mt-3">
          <div className="text-xs uppercase text-zinc-500 mb-1">Contenu</div>
          <ul className="grid md:grid-cols-2 gap-1">
            {boite.items.map((it) => (
              <li key={it.id} className="text-sm flex items-center justify-between gap-2 p-2 rounded-lg bg-zinc-50 dark:bg-zinc-800/60">
                <span className="truncate">{it.nom}{it.notes ? ` – ${it.notes}` : ""}</span>
                <Pill>x{it.quantite}</Pill>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-4">
        <details>
          <summary className="cursor-pointer select-none text-sm text-indigo-600">Afficher le QR / étiquette</summary>
          <div className="mt-3">
            <QRLabel boite={boite} />
          </div>
        </details>
      </div>
    </motion.div>
  );
}

// ====== App principal ======
export default function App() {
  const [db, setDb] = useState(() => loadDB());
  const [q, setQ] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [scanResult, setScanResult] = useState(null);

  useEffect(() => saveDB(db), [db]);

  const boites = db.boites;

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return boites;
    return boites.filter(b =>
      b.nom.toLowerCase().includes(t) ||
      (b.emplacement?.toLowerCase().includes(t)) ||
      (b.notes?.toLowerCase().includes(t)) ||
      b.items.some(it => `${it.nom} ${it.notes ?? ""}`.toLowerCase().includes(t))
    );
  }, [q, boites]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (boite) => { setEditing(boite); setModalOpen(true); };

  const deleteBoite = (id) => {
    if (!confirm("Supprimer définitivement cette boîte ?")) return;
    setDb(prev => ({ ...prev, boites: prev.boites.filter(b => b.id !== id) }));
  };

  const saveBoite = (data) => {
    if (editing) {
      setDb(prev => ({
        ...prev,
        boites: prev.boites.map(b => b.id === editing.id ? { ...b, ...data, majLe: nowISO() } : b)
      }));
    } else {
      const id = uuid();
      const boite = { id, creeLe: nowISO(), majLe: nowISO(), items: [], emplacement: "", notes: "", ...data };
      setDb(prev => ({ ...prev, boites: [boite, ...prev.boites] }));
    }
    setModalOpen(false);
    setEditing(null);
  };

  const exportDB = () => download(`cave-db-${new Date().toISOString().slice(0,10)}.json`, JSON.stringify(db, null, 2));

  const importDB = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.boites)) throw new Error('Fichier invalide');
        setDb(parsed);
        alert("Base importée avec succès ✔");
      } catch (e) {
        alert("Échec de l'import : " + e.message);
      }
    };
    reader.readAsText(file);
  };

  const handleScan = (text) => {
    setScanResult(text);
    let id = null;
    if (typeof text === 'string') {
      const m = text.match(/^box:([a-f0-9\-]+)/i);
      if (m) id = m[1];
      if (!id) {
        try { const j = JSON.parse(text); id = j.id || j.boxId || null; } catch {} 
      }
    }
    if (!id) return;
    const found = db.boites.find(b => b.id === id);
    if (found) {
      setQ(found.nom);
      setTimeout(() => {
        const el = document.getElementById(`boite-${found.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    } else {
      alert("QR reconnu, mais la boîte n'existe pas dans la base locale.");
    }
  };

  // ====== Fonction : export PDF étiquettes (A4, 2x5) ======
  const exportAllQRCards = async (boitesToExport) => {
    if (!boitesToExport || boitesToExport.length === 0) {
      alert("Aucune boîte à exporter");
      return;
    }

    try {
      const pdfDoc = await PDFDocument.create();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

      const pageWidth = 595.28; // A4 points
      const pageHeight = 841.89;
      const cols = 2;
      const rows = 5;
      const cardW = pageWidth / cols;
      const cardH = pageHeight / rows;

      let page = pdfDoc.addPage([pageWidth, pageHeight]);
      let index = 0;

      for (const boite of boitesToExport) {
        const qrDataUrl = await QRCode.toDataURL(`box:${boite.id}`, { margin: 1, width: 300 });
        const qrImage = await pdfDoc.embedPng(qrDataUrl);

        const col = index % cols;
        const row = Math.floor(index / cols) % rows;

        const x = col * cardW;
        const y = pageHeight - (row + 1) * cardH;

        // légère bordure
        page.drawRectangle({ x: x + 6, y: y + 6, width: cardW - 12, height: cardH - 12, borderColor: rgb(0.2, 0.2, 0.2), borderWidth: 0.5 });

        const qrSize = Math.min(120, cardW - 40, cardH - 70);
        const qrX = x + cardW / 2 - qrSize / 2;
        const qrY = y + cardH / 2 - qrSize / 2 + 10;
        page.drawImage(qrImage, { x: qrX, y: qrY, width: qrSize, height: qrSize });

        const text = boite.nom || "(sans nom)";
        const fontSize = 12;
        const textWidth = font.widthOfTextAtSize(text, fontSize);
        const textX = x + cardW / 2 - textWidth / 2;
        const textY = y + 12;
        page.drawText(text, { x: textX, y: textY, size: fontSize, font, color: rgb(0, 0, 0) });

        index++;
        if (index % (cols * rows) === 0 && index < boitesToExport.length) {
          page = pdfDoc.addPage([pageWidth, pageHeight]);
        }
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "etiquettes_cave.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la génération du PDF : " + (e.message || e));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white dark:from-zinc-950 dark:to-zinc-900 text-zinc-900 dark:text-zinc-100">
      <header className="sticky top-0 z-40 backdrop-blur bg-white/70 dark:bg-zinc-950/70 border-b border-zinc-200 dark:border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="text-xl font-bold">📦 Cave QR</div>
          <div className="ml-auto flex items-center gap-2">
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher (nom, contenu, notes…)" className="w-64" />
            <Button onClick={openNew}>+ Nouvelle boîte</Button>
          </div>
        </div>
      </header>

      <main className="px-4 py-6 space-y-8">
        <Section title="Scanner un QR" right={<span className="text-sm text-zinc-500">Pointez la caméra vers l’étiquette</span>}>
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div>
              <Scanner onResult={handleScan} />
            </div>
            <div className="space-y-3">
              <div className="text-sm text-zinc-600 dark:text-zinc-400">Résultat brut du scan :</div>
              <pre className="p-3 rounded-xl bg-zinc-100 dark:bg-zinc-800 overflow-auto text-xs max-h-48">{scanResult ?? "—"}</pre>
              <div className="text-sm text-zinc-600 dark:text-zinc-400">Si le QR correspond à une boîte existante, elle sera mise en avant dans la liste ci-dessous.</div>
            </div>
          </div>
        </Section>

        <Section title="Boîtes" right={<>
          <input id="import" type="file" accept="application/json" className="hidden" onChange={e => e.target.files?.[0] && importDB(e.target.files[0])} />
          <Button variant="ghost" onClick={() => document.getElementById('import').click()}>Importer JSON</Button>
          <Button onClick={exportDB}>Exporter JSON</Button>
          <Button onClick={() => exportAllQRCards(db.boites)}>Exporter étiquettes PDF</Button>
        </>}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {filtered.map((b) => (
                <motion.div key={b.id} layoutId={b.id} id={`boite-${b.id}`}>
                  <BoiteCard boite={b} onEdit={openEdit} onDelete={deleteBoite} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          {filtered.length === 0 && (
            <div className="text-center text-zinc-500 py-8">Aucune boîte trouvée. Créez-en une via « Nouvelle boîte ».</div>
          )}
        </Section>
      </main>

      <footer className="px-4 py-8 text-center text-xs text-zinc-500">
        Données stockées en local dans votre navigateur. Pensez à exporter régulièrement le JSON pour sauvegarder.
      </footer>

      <EditBoiteModal
        open={modalOpen}
        initial={editing}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSave={saveBoite}
      />
    </div>
  );
}
